import { useCallback, type MutableRefObject } from "react";
import { doc, getDoc, runTransaction, serverTimestamp, type Firestore } from "firebase/firestore";
import { normalizeSerialKey, splitSerialParts } from "../components/editModalFunctions";
import { toSlug } from "../lib/formatters";

type OpenEditDeps = {
  db: Firestore;
  isCustomerPortal: boolean;
  currentUserString: () => string | null;
  startLockHeartbeat: (id: string) => void;
  startLockWatcher: (id: string, readOnly: boolean) => void;
  setEditIsReadOnly: (val: boolean) => void;
  setEditInvoiceReportId: (val: string | null) => void;
  setEditId: (val: string | null) => void;
  setEditForm: (val: any) => void;
  setIsEditOpen: (val: boolean) => void;
  editOriginalRef: MutableRefObject<{ serial: string; serialBaseKey: string | null; itemId: string | null; }>;
  lastSerialAllocRef: MutableRefObject<{ changed: boolean; serialBaseKey: string; serialVisit: number; displaySerial: string; } | null>;
  toMillis: (v: any) => number | null;
  LOCK_STALE_MS: number;
  openedReadOnlyDueToDeleteRef: MutableRefObject<boolean>;
  setDeleteClearedWhileOpen: (val: boolean) => void;
};

export function useOpenEdit({
  db,
  isCustomerPortal,
  currentUserString,
  startLockHeartbeat,
  startLockWatcher,
  setEditIsReadOnly,
  setEditInvoiceReportId,
  setEditId,
  setEditForm,
  setIsEditOpen,
  editOriginalRef,
  lastSerialAllocRef,
  toMillis,
  LOCK_STALE_MS,
  openedReadOnlyDueToDeleteRef,
  setDeleteClearedWhileOpen,
}: OpenEditDeps) {
  return useCallback(async (item: any): Promise<void> => {
    try {
      const ref = doc(db, "itInventory", item.id);
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        alert("Kunde inte öppna “ posten finns inte längre.");
        return;
      }
      const data = snap.data() as any;

      try {
        const originalSerial = String(data?.serial ?? "");
        const derivedBaseKey =
          data?.serialBaseKey
            ? String(data.serialBaseKey)
            : (data?.serialBase ? String(data.serialBase) : (
              originalSerial
                ? normalizeSerialKey(splitSerialParts(originalSerial).base)
                : null
            ));

        lastSerialAllocRef.current = null;

        editOriginalRef.current = {
          serial: originalSerial,
          serialBaseKey: derivedBaseKey || null,
          itemId: item.id ?? null,
        };
      } catch (e) {
        console.error("[editOriginalRef] init misslyckades:", e);
        editOriginalRef.current = { serial: "", serialBaseKey: null, itemId: item.id ?? null };
      }

      setEditIsReadOnly(false);
      setEditInvoiceReportId(null);

      const isInvoiced = !!data.invoiceReportId;
      const me = currentUserString();
      const deleteOwnedByOther = !!data.deletePending && !!data.deleteMarkedBy && data.deleteMarkedBy !== me;
      const readOnly = isCustomerPortal || isInvoiced || deleteOwnedByOther;

      openedReadOnlyDueToDeleteRef.current = deleteOwnedByOther;
      setDeleteClearedWhileOpen(false);

      setEditIsReadOnly(readOnly);
      if (isInvoiced) setEditInvoiceReportId(String(data.invoiceReportId));

      if (!isCustomerPortal && data.markedForInvoice === true && !isInvoiced) {
        alert("Detta objekt är markerat för fakturering och är tillfälligt spärrat för editering. Avmarkera i Fakturering för att öppna igen.");
        return;
      }
      if (deleteOwnedByOther) {
        alert(`Posten är markerad för radering av ${data.deleteMarkedBy}. Läser i skrivskyddat läge.`);
      }

      setEditId(item.id);
      setEditForm({
        assetId: typeof data.assetId === "number" ? data.assetId : undefined,
        blanccoReportUuid: (data as any).blanccoReportUuid ?? null,
        orderNumber: data.orderNumber || "",
        manufacturer: data.manufacturer || "",
        model: data.model || "",
        serial: data.serial || "",
        chargerIncluded: !!data.chargerIncluded,
        adapterYesNo: (data as any).adapterYesNo ?? (data.chargerIncluded ? "Yes" : "No"),
        damageNotes: data.damageNotes || "",
        createdBy: data.createdBy || data.initials || null,
        createdAt: data.createdAt || null,
        auditLog: Array.isArray(data.auditLog) ? data.auditLog : [],
        photos: data.photos || {},
        completed: !!data.completed,
        completedAt: data.completedAt || null,
        deletePending: !!data.deletePending,
        deleteMarkedBy: data.deleteMarkedBy || null,
        deleteMarkedAt: data.deleteMarkedAt || null,
        completedBy: data.completedBy || null,
        reuse: !!data.reuse,
        resold: !!data.resold,
        scrap: !!data.scrap,
        grade: data.grade || "",
        productType: data.productType || "",
        productTypeId: data.productTypeId || (data.productType ? toSlug(data.productType) : ""),
        salesBatchId: data.salesBatchId || "",
        salesBatchName: data.salesBatchName || "",
        salesKolliId: data.salesKolliId || "",
        salesKolliName: data.salesKolliName || "",
        salesCpu: data.salesCpu || "",
        salesStorage: data.salesStorage || "",
        salesRam: data.salesRam || "",
      } as EditFormState);
      setIsEditOpen(true);

      // Om assetId saknas (skrivs av Cloud Function strax efter create), hämta om några gånger
      if (data.assetId === undefined || data.assetId === null) {
        const MAX_RETRIES = 5;
        const WAIT_MS = 500;
        void (async () => {
          for (let i = 0; i < MAX_RETRIES; i++) {
            await new Promise((res) => setTimeout(res, WAIT_MS));
            const fresh = await getDoc(ref);
            const assetId = fresh.exists() ? (fresh.data() as any).assetId : undefined;
            if (typeof assetId === "number") {
              setEditForm((prev: EditFormState) => ({ ...prev, assetId }));
              break;
            }
          }
        })();
      }

      if (!readOnly && !data.completed) {
        try {
          await runTransaction(db, async (tx) => {
            const fresh = await tx.get(ref);
            if (!fresh.exists()) throw new Error("Posten finns inte längre.");
            const cur = fresh.data() as any;

            if (cur.invoiceReportId) throw new Error("Enheten är fakturerad och permalåst.");

            const heldByOther = !!cur.lockedBy && cur.lockedBy !== currentUserString();
            const lockedAtMs = toMillis(cur.lockedAt);
            const isStale = lockedAtMs === null ? true : (Date.now() - lockedAtMs) > LOCK_STALE_MS;
            if (heldByOther && !isStale) {
              throw new Error(`Posten redigeras av ${cur.lockedBy}.`);
            }

            tx.update(ref, { lockedBy: currentUserString(), lockedAt: serverTimestamp() } as any);
          });
          startLockHeartbeat(item.id);
        } catch (e: any) {
          alert(e?.message ?? "Kunde inte låsa posten för redigering.");
          setIsEditOpen(false);
          setEditId(null);
          return;
        }
      }
      startLockWatcher(item.id, readOnly);
    } catch (e: any) {
      console.error(e);
    }
  }, [
    db,
    isCustomerPortal,
    currentUserString,
    startLockHeartbeat,
    startLockWatcher,
    setEditIsReadOnly,
    setEditInvoiceReportId,
    setEditId,
    setEditForm,
    setIsEditOpen,
    editOriginalRef,
    lastSerialAllocRef,
    toMillis,
    LOCK_STALE_MS,
  ]);
}
